import { createStore } from 'vuex';
import dealerAuth from './dealerAuthModule';
import { getCart, putCart } from '../api/cart';

const store = createStore({
    modules: {
        auth: dealerAuth
    },
    state: () => ({
        cart: []  // [{ variantId, sku, title, qty, retailPrice, dealerPrice, vendor, image }]
    }),
    getters: {
        cartItems: state => state.cart,
        cartCount: state => state.cart.reduce((sum, i) => sum + i.qty, 0),
        cartTotal: state => state.cart.reduce((sum, i) => sum + (i.dealerPrice ?? i.retailPrice) * i.qty, 0)
    },
    mutations: {
        addToCart(state, item) {
            const existing = state.cart.find(i => i.variantId === item.variantId);
            if (existing) {
                existing.qty += item.qty || 1;
            } else {
                state.cart.push({ ...item, qty: item.qty || 1 });
            }
        },
        removeFromCart(state, variantId) {
            state.cart = state.cart.filter(i => i.variantId !== variantId);
        },
        updateQty(state, { variantId, qty }) {
            const item = state.cart.find(i => i.variantId === variantId);
            if (item) item.qty = qty;
        },
        clearCart(state) {
            state.cart = [];
        },
        setCart(state, cart) {
            state.cart = cart;
        },
        updateCartItemPricing(state, { sku, ...fields }) {
            const item = state.cart.find(i => i.sku === sku);
            if (item) Object.assign(item, fields);
        }
    }
});

// Cart is persisted server-side, scoped to the authenticated dealer. Whenever the active
// dealer changes (login, SSO, admin token, logout, or session expiry all funnel through
// auth/setDealer), fetch that dealer's cart from the server instead of whatever was last
// displayed. Persist on every cart change; updateQty is debounced since the qty stepper
// can fire several times in quick succession, the rest persist immediately.
let lastDealerId = null;
let qtyDebounceTimer = null;
store.subscribe((mutation, state) => {
    const dealerId = state.auth.dealer ? state.auth.dealer.id : null;

    if (mutation.type === 'auth/setDealer') {
        if (dealerId !== lastDealerId) {
            lastDealerId = dealerId;
            if (dealerId) {
                getCart().then(items => store.commit('setCart', items)).catch(() => store.commit('setCart', []));
            } else {
                store.commit('setCart', []);
            }
        }
        return;
    }

    if (!dealerId) return;

    if (mutation.type === 'updateQty') {
        clearTimeout(qtyDebounceTimer);
        qtyDebounceTimer = setTimeout(() => {
            putCart(state.cart).catch(() => {});
        }, 400);
        return;
    }

    if (['addToCart', 'removeFromCart', 'clearCart', 'updateCartItemPricing'].includes(mutation.type)) {
        putCart(state.cart).catch(() => {});
    }
});

export default store;
